/**
 * Show a list of subscriptions.  Used in the Subscriptions tab of the Customer page, and on the MyApps page.
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, FormControlLabel, Grid, 
         Switch, TablePagination, Tooltip, Typography} from '@mui/material';
import SubscriptionsList from './SubscriptionsList';
import LoadingSpinner from '../ui/LoadingSpinner';
import Add from '@mui/icons-material/Add';
import ServerError from '../ui/ServerError';
import SearchField from '../ui/SearchField';
import ConfirmationDialog from '../ui/ConfirmationDialog';

const abEnvironment = new AbEnvironment();

const SubscriptionsQuery = graphql`
query SubscriptionsQuery($customerAccountId: UUID, $appName: String, $pOrder: Int, $authOnly: Int, $showCanceled: Int, $orderBy: Int, $direction: Int, $first: Int, $offset: Int) {
  getcustomersubscriptioncharges4(customerAccountId: $customerAccountId, appName: $appName, paymentOrder: $pOrder, authOnly: $authOnly, showCanceled: $showCanceled, orderby: $orderBy, direction: $direction, first: $first, offset: $offset) {
    totalCount
    ...SubscriptionsList_sl
  }
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
      // marginTop: -32,
    },
    flex: {
      flex: 1,
    },
    divider: {
      marginTop: 4,
      marginBottom: 4,
      marginLeft: 0,
    },
    button: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    dateField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    textField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      marginTop: 4,
      width: 180,
    },
    textField2: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      marginTop: 3,
      width: 160,
    },
      paper1: {
      padding: 16,
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
    paper2: {
      padding: 12,
      textAlign: 'left',
      color: theme.palette.text.secondary,
    },
    paper3: {
      padding: 16,
      textAlign: 'center',
      color: theme.palette.text.secondary,
    }
});

class Subscriptions extends Component {

  constructor(props) {
    super(props);

    this.state = {
      customerAccountId: this.props.customerAccountId,
      orderBy:           0,
      orderDir:          1,  
      appName:           '', 
      showCanceled:      0,
      page:              0,
      rowsPerPage:       10,
      requestTransfer:   false,
    };  
  }

  componentDidMount() {

    //if passed, look for a special url value to pre-position on subs.
    if (this.props.location && this.props.location.search) {
      let params = new URLSearchParams(this.props.location.search);
      let fromSOConfirm = params.get('confirm');
      if (fromSOConfirm === 'true') {
        this.setState({orderBy: 1, orderDir: 0});
      }
    } 
  }

  handleChange = name => event => {
    this.setState({
      [name]: event.target.value,
      page: 0,
    });
  };

  handleChangePage = (event, page) => {
    this.setState({page: page});
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value, page : 0});
  }

  goTo(route) {
    this.props.history.push(`/${route}`);
  }

  setParentTab = (tv) => {
    if (this.props.setTabVal) {
      this.props.setTabVal(tv);
    }
  }

  toggleOrder = (ob) => {
    let newDir = 0;
    if (ob === this.state.orderBy) {
      if (this.state.orderDir === 0) {
        newDir = 1;
      }
    } else {
      newDir = 1;
    }

    this.setOrderBy(ob, newDir);
  } 

  setOrderBy = (ob, dir) => {
    let thedir = dir;
    if (dir === 'asc') {
      thedir = 1;
    } else if (dir === 'desc') {
      thedir = 0;
    }
    if (thedir !== 0 && thedir !== 1) {
      thedir = 1;
    }
    this.setState({orderBy: ob, orderDir: thedir, page : 0});
  }

  determineBuySubscription = () => {

    //pg has "myApps" when passed from looking at someone's own subscriptions
    //so when it is null, it means this component is used from customer.js

    let thePage = this.props.pg;
    let useSOProcess = false;
    let lockdown = false;
    if (this.props.auth.isAuthenticatedReseller() && abEnvironment.getUserAttribute('a_useBudgets') !== '0' && thePage == null) {
      if (this.props.customerUseBudgets >= 1) {
        useSOProcess = true;
      } else {
        lockdown = true;
      }
    } else if (thePage != null) {
      //looking at myApps
      lockdown = true;
    }

    if (useSOProcess) {
      this.goToSO(); 
    } else {
      let link = this.props.custAppLink;
      if (lockdown) {
        link = link + '&ld=true';
      }
      this.goTo(link);
    }
  }
  
  goToSO() {
    let propsToCache = {
      accountId: this.props.customerAccountId,
      companyName: this.props.customerName,
      contactLastName: this.props.contactLastName, 
      contactFirstName: this.props.contactFirstName, 
      contactEmail: this.props.contactEmail
    };
    localStorage.setItem('soCustomerParms', JSON.stringify(propsToCache));

    this.props.history.push(`/subscriptionorder`);
  }

  goToFunding = () => {
    if (this.props.setTabVal) {
      this.props.setTabVal(1);
    }  else if (this.props.pscount != null && this.props.pscount === 0) {
      // Go to the funding page
      this.props.history.push("/fundingsources");
    }
  }

  requestTransfer() {
    this.setState({requestTransfer: false});
  }

  render() {
    const { isAuthenticated, isAuthenticatedCustomer, isAuthenticatedAppBind, isUnauthenticatedCustomer, needAssent, logError } = this.props.auth;
    // const classes = this.props.classes;

    if (!isAuthenticated() || needAssent()) { 
      return (<Redirect to='/home' />);
    }    
    let theAppSearch = '%';
    if (this.state.appName !== '') {
      theAppSearch = '%' + this.state.appName + '%';
    }

    // Determine the offset
    let offset = 0;
    if (this.state.page > 0) {
      offset = this.state.page * this.state.rowsPerPage
    }
    let first = this.state.rowsPerPage;

    let theRole = "reseller";
    if (isAuthenticatedCustomer()) {
      theRole = "customer";
    } else if (isUnauthenticatedCustomer()) {
      // theRole = "unauthcustomer";
      theRole = "customer";
    }

    // The authOnly flag was intended to be used to pull either:
    // 0: Paid subscriptions (bc_status <> 4)
    // 1: Auth-only subscriptions (bc_status = 4)
    // 2: All subscriptions (don't check bc_status)
    //
    // As of 11/2023, this does not seem relevant. This query should only be used to show all of "my" subscriptions
    // for a reseller (customer now has a different tabbed experience), or for a reseller to see all of a customer's
    // subscriptions. In both of these cases, all subscriptions should be shown, so default to 2.
    let authOnly = 2; 

    // When called from CustomerAppList, props.pg will be set to "myApps"
    let thePage = this.props.pg;
    let resellerViewingCustomerRestricted = false;
    if (thePage == null) {
      thePage = "custSubs";
      try {
        let rUseBudgets = Number.parseInt(abEnvironment.getUserAttribute('a_useBudgets'));
        if (rUseBudgets === 2) {
          resellerViewingCustomerRestricted = true;
        }
      } catch (nErr) {
        // Swallow
      }
      authOnly = 2;
    }

    let manageButtons = "show";
    if (isUnauthenticatedCustomer() || isAuthenticatedAppBind()) {
      // Unauthenticated customers can't manage subscriptions
      manageButtons = "hide";
    } 

    let showForSelf = false;
    if (this.props.pscount != null && this.props.pscount === 0) {
      // Link to connect funding source
      showForSelf = true;
    }

    let buySubscription = 'contained';
    if (this.props.showInvite || showForSelf) {
      buySubscription = 'outlined';
    }

    let paymentOrder = null;
    if (thePage === "myApps" && theRole === "customer") {
      // Customers should not see subscriptions that the reseller pays for
      paymentOrder = 2;
    }

    let showCanceled = null;
    if (this.state.showCanceled === 1) {
      //show all subscriptions including canceled
      showCanceled = 1;
    }

    // This is demoware - the flag should only be set on a customer in POC
    let specialMenu = false;
    if (isUnauthenticatedCustomer() && (abEnvironment.getFlagValue("allow_customer_transfer")) && (abEnvironment.getFlagValue("allow_customer_transfer").flagValue === 1)) {
      specialMenu = true;
    }
    
    return (
      <Grid container spacing={1} direction="row" >
        {specialMenu &&
        <Grid container spacing={2} direction="row" justifyContent="space-between">
          <Grid item>
            <Typography variant="h6">
            { thePage !== "myApps" ? 'Manage subscriptions for your customer' : 'Subscriptions' }
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title='Take control of your subscriptions'>
              <Button
                color="primary"
                variant="outlined"
                onClick={() => {
                  this.setState({ requestTransfer: true })
                }}>
                Request Transfer
              </Button>
            </Tooltip>
          </Grid>
        </Grid>}

        <Grid item xs={12}>
          <Grid container spacing={2} direction="row" alignItems="center">
            <Grid item>
              <SearchField
                id="appName"
                label="Search by app name"
                value={this.state.appName}
                onChange={this.handleChange('appName')}
              />
            </Grid>

            <Grid item>
              <FormControlLabel
                style={{marginBottom: -14}}
                control={
                  <Switch
                    checked={this.state.showCanceled === 1}
                    onChange={(e, checked) => {
                      if (checked) {
                        this.setState({ showCanceled: 1, page: 0 });
                      } else {
                        this.setState({ showCanceled: 0, page: 0 });
                      }
                    }}
                  />
                }
                label="Show cancelled"
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <QueryRenderer
            environment={abEnvironment.getEnvironment()}
            query={SubscriptionsQuery}
            variables={{
              customerAccountId: this.state.customerAccountId,
              appName: theAppSearch,
              pOrder: paymentOrder,
              showCanceled: showCanceled,
              authOnly: authOnly,
              orderBy: this.state.orderBy,
              direction: this.state.orderDir,
              first: first,
              offset: offset
            }}
            render={({ error, props }) => {
              if (error) {
                logError(error.message);
                return (
                  <ServerError />
                );
              } else if (props) {
                // Determine offset information
                let totalCount = props.getcustomersubscriptioncharges4.totalCount;

                let parentAccountName = '';
                if (this.props.parentAccount && this.props.parentAccount.accountName) {
                  parentAccountName = this.props.parentAccount.accountName;
                }
                return (
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <SubscriptionsList auth={this.props.auth}
                        sl={props.getcustomersubscriptioncharges4} 
                        theRole={theRole}
                        transferredCustomer={this.props.transferredCustomer}
                        account={this.props.account}
                        thePage={thePage}
                        history={this.props.history}
                        orderBy={this.state.orderBy}
                        orderDir={this.state.orderDir}
                        toggleOrder={this.toggleOrder} 
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={0} direction="row-reverse" justifyContent="space-between" alignItems="center">
                        <Grid item>
                          <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={totalCount}
                            rowsPerPage={this.state.rowsPerPage}
                            page={this.state.page}
                            backIconButtonProps={{
                              'aria-label': 'Previous Page',
                            }}
                            nextIconButtonProps={{
                              'aria-label': 'Next Page',
                            }}
                            onPageChange={this.handleChangePage}
                            onRowsPerPageChange={this.handleChangeRowsPerPage}
                          />
                        </Grid>
                        <Grid item>
                          {manageButtons === "show" &&
                          <Grid container spacing={2} direction="row" justifyContent="space-between">
                            <Grid item>
                              <Grid container spacing={2} direction="row" justifyContent="flex-start">
                                {(this.props.showInvite || showForSelf) && !resellerViewingCustomerRestricted &&
                                <Grid item>
                                  <Tooltip title='A funding source is needed to pay for subscriptions.'>
                                    <Button
                                        color="primary"
                                        variant="contained" 
                                        disabled={this.props.accountStatus === 1}
                                        onClick={() => {
                                          this.goToFunding();
                                        }}>
                                      Connect funding source
                                    </Button> 
                                  </Tooltip>     
                                </Grid>
                                }
                                <Grid item>
                                  <Button
                                      color="primary"
                                      variant={buySubscription}
                                      disabled={this.props.accountStatus === 1}
                                      onClick={() => {
                                        this.determineBuySubscription(); 
                                      }}>
                                      <Add />&nbsp;Add a Subscription
                                  </Button>      
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>}
                        </Grid>                    
                      </Grid>
                    </Grid>

                    <ConfirmationDialog
                      open={this.state.requestTransfer}
                      onClose={() => { this.setState({ requestTransfer: false }); }}
                      onConfirm={() => { this.requestTransfer(); }}
                      onCancel={() => { }}
                      title={"Transfer subscriptions"}
                      cancelBtn="Cancel"
                      okBtn="Yes, proceed"
                      irreversibleAction={true}
                    >
                      <Typography>
                        Take control of subscriptions from {parentAccountName}
                      </Typography>
                      <Typography style={{ marginTop: '16px' }}>
                        When you take control of your subscriptions, you'll be responsible for managing your SaaS subscriptions. {parentAccountName} won't have access to your subscriptions, and won't be able to manage current subscriptions or create new subscriptions on your behalf. You should only do this if you are ending your business relationship with {parentAccountName}.
                      </Typography>

                    </ConfirmationDialog>

                  </Grid>
                )
              }
              return (
                <LoadingSpinner />
              );
            }}
          />
        </Grid>
      </Grid>
    );
  }
}
  
Subscriptions.propTypes = {
  classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(Subscriptions);
